import {cloneDeep} from "lodash";

export class School {
    constructor(data) {
        this.schoolId = null;
        this.name = null;
        this.lat = null;
        this.lon = null;
        this.logo = null;
        this.startBellTimes = []// seconds for each day of week
        this.endBellTimes = []// seconds for each day of week
        Object.assign(this, data)
    }

    static clone(data) {
        const school = new School();
        Object.assign(school, cloneDeep(data));
        return school;
    }

}

/**
 * Find all school stops that have a given stop as a linked stop.
 *
 * @param {string} stopId - The stop ID to check for school linkage
 * @param {Object} allStops - Map of stopId -> Stop objects
 * @returns {Array} Array of school stops that have this stop as a linked stop
 *
 * @example
 * const schools = findLinkedSchoolStops('stop-123', allStops);
 * // Returns: [{ stopId: 'school-1', stopName: 'Magdalene College', startBell: 28800, ... }]
 */
export function findLinkedSchoolStops(stopId, allStops) {
    if (!stopId || !allStops) return [];

    return Object.values(allStops).filter(s =>
        s.stopType === 'school' &&
        s.linkedStops?.some(ls => ls.stopId === stopId)
    );
}

/**
 * Build a lookup map from stopId -> school stop for efficient O(1) school detection.
 * Use this when checking multiple stops in a loop to avoid O(N*M) complexity.
 *
 * @param {Object} allStops - Map of stopId -> Stop objects
 * @returns {Object} Map of stopId -> first linked school stop (or undefined if not linked)
 *
 * @example
 * const schoolByStop = buildSchoolStopLookup(allStops);
 * const linkedSchool = schoolByStop['stop-123']; // O(1) lookup
 */
export function buildSchoolStopLookup(allStops) {
    if (!allStops) return {};

    const lookup = {};
    Object.values(allStops).forEach(stop => {
        if (stop.stopType === 'school' && stop.linkedStops) {
            stop.linkedStops.forEach(ls => {
                // Only store first linked school per stop
                if (!lookup[ls.stopId]) {
                    lookup[ls.stopId] = stop;
                }
            });
        }
    });
    return lookup;
}

/**
 * Check if an arrival time is within a school's start bell window.
 * The start bell window is [startBell - startBellWindow, startBell].
 *
 * @param {number} arrivalSecs - Arrival time in seconds since midnight
 * @param {Object} schoolStop - School stop object with startBell and startBellWindow
 * @returns {boolean|null} true if within window, false if outside, null if no bell configured
 *
 * @example
 * // School bell at 08:30 (30600s) with 15min window (900s)
 * // Window is [29700, 30600] or [08:15, 08:30]
 * isWithinStartBellWindow(30000, { startBell: 30600, startBellWindow: 900 }); // true (08:20)
 * isWithinStartBellWindow(31000, { startBell: 30600, startBellWindow: 900 }); // false (08:36)
 */
export function isWithinStartBellWindow(arrivalSecs, schoolStop) {
    if (!schoolStop?.startBell || !schoolStop?.startBellWindow) return null;
    if (arrivalSecs === undefined || arrivalSecs === null) return null;

    const windowStart = schoolStop.startBell - schoolStop.startBellWindow;
    const windowEnd = schoolStop.startBell;
    return arrivalSecs >= windowStart && arrivalSecs <= windowEnd;
}

/**
 * Check if a departure time is within a school's end bell window.
 * The end bell window is [endBell, endBell + endBellWindow].
 *
 * @param {number} departSecs - Departure time in seconds since midnight
 * @param {Object} schoolStop - School stop object with endBell and endBellWindow
 * @returns {boolean|null} true if within window, false if outside, null if no bell configured
 *
 * @example
 * // School end bell at 15:30 (55800s) with 15min window (900s)
 * // Window is [55800, 56700] or [15:30, 15:45]
 * isWithinEndBellWindow(56000, { endBell: 55800, endBellWindow: 900 }); // true (15:33)
 * isWithinEndBellWindow(57000, { endBell: 55800, endBellWindow: 900 }); // false (15:50)
 */
export function isWithinEndBellWindow(departSecs, schoolStop) {
    if (!schoolStop?.endBell || !schoolStop?.endBellWindow) return null;
    if (departSecs === undefined || departSecs === null) return null;

    const windowStart = schoolStop.endBell;
    const windowEnd = schoolStop.endBell + schoolStop.endBellWindow;
    return departSecs >= windowStart && departSecs <= windowEnd;
}

/**
 * Check if stop times align with a school's bell windows.
 * Returns true if either start bell (arrival) or end bell (departure) window is satisfied.
 *
 * @param {number} arrivalSecs - Arrival time in seconds since midnight
 * @param {number} departSecs - Departure time in seconds since midnight (optional)
 * @param {Object} schoolStop - School stop object with bell time configuration
 * @returns {{ isValid: boolean, isValidForStartBell: boolean|null, isValidForEndBell: boolean|null, hasBellTimes: boolean }}
 *
 * @example
 * const result = validateBellWindow(30000, 30100, schoolStop);
 * // result: { isValid: true, isValidForStartBell: true, isValidForEndBell: false, hasBellTimes: true }
 */
export function validateBellWindow(arrivalSecs, departSecs, schoolStop) {
    const hasStartBell = !!(schoolStop?.startBell && schoolStop?.startBellWindow);
    const hasEndBell = !!(schoolStop?.endBell && schoolStop?.endBellWindow);
    const hasBellTimes = hasStartBell || hasEndBell;

    if (!hasBellTimes) {
        return { isValid: true, isValidForStartBell: null, isValidForEndBell: null, hasBellTimes: false };
    }

    const isValidForStartBell = isWithinStartBellWindow(arrivalSecs, schoolStop);
    const isValidForEndBell = isWithinEndBellWindow(departSecs, schoolStop);

    // Valid if either window is satisfied (when configured)
    const isValid = (isValidForStartBell === true) || (isValidForEndBell === true);

    return { isValid, isValidForStartBell, isValidForEndBell, hasBellTimes };
}
